import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Archive, Clock, Pencil, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { archiveEntry, SHORT_STOOL_LABEL } from './entriesApi';

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const pillClass = (ok) =>
  ok ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-100 border-slate-300 text-slate-600';

export function BowelChartEntryList({
  entries, participantId, chartId, canEdit, canArchive,
  onEdit, onChanged, showArchived,
}) {
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [pending, setPending] = useState(false);

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      setPending(true);
      await archiveEntry(participantId, chartId, archiveTarget.id);
      toast.success('Entry archived');
      setArchiveTarget(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to archive entry');
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <div className="rounded-md border overflow-hidden" data-testid="entries-list">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Date / time</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Opened</TableHead>
              <TableHead>Stool</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Continence</TableHead>
              <TableHead>Pain</TableHead>
              <TableHead>Notes</TableHead>
              {showArchived && <TableHead>Status</TableHead>}
              {(canEdit || canArchive) && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map(e => {
              const isArchived = e.status === 'archived';
              return (
                <TableRow key={e.id} data-testid={`entry-row-${e.id}`} className={isArchived ? 'opacity-60' : ''}>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      {formatDateTime(e.entry_date)}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs capitalize">{e.shift_period || '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${pillClass(e.bowel_opened)}`}>
                      {e.bowel_opened ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {e.bowel_opened ? (SHORT_STOOL_LABEL[e.stool_type] || '—') : '—'}
                  </TableCell>
                  <TableCell className="text-xs capitalize">{e.bowel_opened ? (e.amount || '—') : '—'}</TableCell>
                  <TableCell className="text-xs capitalize">{e.bowel_opened ? (e.continence || '—') : '—'}</TableCell>
                  <TableCell>
                    {e.pain_discomfort ? (
                      <Badge variant="outline" className="text-[10px] bg-rose-50 border-rose-300 text-rose-700">
                        <AlertCircle className="w-2.5 h-2.5 mr-0.5" />Yes
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">No</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs max-w-[220px]">
                    <span className="line-clamp-2" title={e.notes || ''}>{e.notes || '—'}</span>
                  </TableCell>
                  {showArchived && (
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${isArchived ? 'bg-slate-100 border-slate-300 text-slate-600' : 'bg-emerald-50 border-emerald-300 text-emerald-700'}`}>
                        {isArchived ? 'Archived' : 'Active'}
                      </Badge>
                    </TableCell>
                  )}
                  {(canEdit || canArchive) && (
                    <TableCell className="text-right whitespace-nowrap">
                      {!isArchived && canEdit && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => onEdit?.(e)}
                          data-testid={`entry-edit-btn-${e.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                        </Button>
                      )}
                      {!isArchived && canArchive && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 text-xs text-muted-foreground hover:text-red-600"
                          onClick={() => setArchiveTarget(e)}
                          data-testid={`entry-archive-btn-${e.id}`}
                        >
                          <Archive className="w-3.5 h-3.5 mr-1" />Archive
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="entry-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive entry</AlertDialogTitle>
            <AlertDialogDescription>
              Archive this entry recorded on <strong>{formatDateTime(archiveTarget?.entry_date)}</strong>? Archived entries are retained for audit but hidden from the active list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={pending}
              data-testid="entry-archive-confirm-btn"
            >
              {pending ? 'Archiving…' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
